import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, CalendarDays, Clock, CheckCircle2, Video, ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";

interface Slot { start: string; end: string; label: string; }
interface Day { date: string; slots: Slot[]; }

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  language?: string;
  defaultEmail?: string;
  defaultName?: string;
  defaultCompany?: string;
  websiteUrl?: string;
}

export default function WalkthroughBookingDialog({
  open, onOpenChange, language = "el",
  defaultEmail = "", defaultName = "", defaultCompany = "", websiteUrl,
}: Props) {
  const isEl = language?.toLowerCase().startsWith("el");
  const t = (el: string, en: string) => (isEl ? el : en);
  const { toast } = useToast();

  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState<Day[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);

  const [name, setName] = useState(defaultName);
  const [email, setEmail] = useState(defaultEmail);
  const [company, setCompany] = useState(defaultCompany);
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<{ meetLink: string | null; htmlLink?: string } | null>(null);

  useEffect(() => {
    if (!open) return;
    setSuccess(null);
    setSelectedSlot(null);
    setLoading(true);
    supabase.functions.invoke("walkthrough-availability", { body: {} })
      .then(({ data, error }) => {
        if (error) throw error;
        const ds: Day[] = data?.days ?? [];
        setDays(ds);
        setSelectedDate(ds[0]?.date ?? null);
      })
      .catch((e) => {
        console.error(e);
        toast({ title: t("Δεν φορτώθηκαν τα διαθέσιμα ραντεβού", "Could not load availability"), variant: "destructive" });
      })
      .finally(() => setLoading(false));
  }, [open]);

  useEffect(() => {
    if (open) {
      setName(defaultName);
      setEmail(defaultEmail);
      setCompany(defaultCompany);
    }
  }, [open, defaultEmail, defaultName, defaultCompany]);

  const activeDay = useMemo(
    () => days.find(d => d.date === selectedDate) ?? null,
    [days, selectedDate]
  );

  const formatDayLabel = (iso: string) => {
    const d = new Date(iso + "T12:00:00");
    return new Intl.DateTimeFormat(isEl ? "el-GR" : "en-GB", {
      weekday: "short", day: "numeric", month: "short",
    }).format(d);
  };

  const submit = async () => {
    if (!selectedSlot) return;
    if (name.trim().length < 2) {
      toast({ title: t("Συμπληρώστε το όνομά σας", "Please enter your name"), variant: "destructive" });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast({ title: t("Μη έγκυρο email", "Invalid email"), variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("walkthrough-book", {
        body: {
          start: selectedSlot.start,
          end: selectedSlot.end,
          name, email, company, phone, notes,
          website: websiteUrl,
          language: isEl ? "el" : "en",
        },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      setSuccess({ meetLink: (data as any).meetLink, htmlLink: (data as any).htmlLink });
    } catch (e: any) {
      const msg = e?.message?.includes("slot_unavailable")
        ? t("Το ραντεβού δεν είναι πλέον διαθέσιμο. Επιλέξτε άλλη ώρα.", "That slot is no longer available. Please pick another time.")
        : t("Κάτι πήγε στραβά. Δοκιμάστε ξανά.", "Something went wrong. Please try again.");
      toast({ title: msg, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto">
        {success ? (
          <div className="py-8 text-center space-y-4">
            <CheckCircle2 className="w-14 h-14 mx-auto text-primary" />
            <DialogTitle className="text-2xl">
              {t("Το ραντεβού σας κλείστηκε!", "Your call is booked!")}
            </DialogTitle>
            <p className="text-muted-foreground">
              {t(
                "Σας στείλαμε πρόσκληση Google Calendar στο email σας.",
                "We just sent a Google Calendar invite to your email."
              )}
            </p>
            {success.meetLink && (
              <a
                href={success.meetLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-primary font-medium hover:underline"
              >
                <Video className="w-4 h-4" /> {t("Σύνδεσμος Google Meet", "Google Meet link")}
              </a>
            )}
            <div className="pt-2">
              <Button onClick={() => onOpenChange(false)}>{t("Κλείσιμο", "Close")}</Button>
            </div>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-primary" />
                {selectedSlot
                  ? t("Επιβεβαιώστε τα στοιχεία σας", "Confirm your details")
                  : t("Επιλέξτε ημερομηνία & ώρα", "Pick a date & time")}
              </DialogTitle>
              <DialogDescription>
                {t(
                  "20-λεπτη δωρεάν στρατηγική συνεδρία με τον Δημήτρη μέσω Google Meet.",
                  "20-minute free strategy call with Dimitris via Google Meet."
                )}
              </DialogDescription>
            </DialogHeader>

            {loading ? (
              <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
            ) : !selectedSlot ? (
              <div className="space-y-4 pt-2">
                {days.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    {t("Δεν υπάρχουν διαθέσιμες ώρες αυτή τη στιγμή.", "No available times right now.")}
                  </p>
                ) : (
                  <>
                    <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
                      {days.map((d) => (
                        <button
                          key={d.date}
                          onClick={() => setSelectedDate(d.date)}
                          className={cn(
                            "snap-start shrink-0 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors",
                            selectedDate === d.date
                              ? "bg-primary text-primary-foreground border-primary"
                              : "bg-background border-border hover:bg-muted"
                          )}
                        >
                          {formatDayLabel(d.date)}
                        </button>
                      ))}
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 pt-2">
                      {(activeDay?.slots ?? []).map((s) => (
                        <button
                          key={s.start}
                          onClick={() => setSelectedSlot(s)}
                          className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg border border-border bg-background hover:border-primary hover:text-primary text-sm font-medium transition-colors"
                        >
                          <Clock className="w-3.5 h-3.5" /> {s.label}
                        </button>
                      ))}
                      {activeDay && activeDay.slots.length === 0 && (
                        <p className="col-span-full text-sm text-muted-foreground text-center py-4">
                          {t("Καμία διαθέσιμη ώρα.", "No available times.")}
                        </p>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground text-center">
                      {t("Ώρα Ελλάδας (Europe/Athens)", "Times shown in Athens time (Europe/Athens)")}
                    </p>
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-4 pt-2">
                <button
                  onClick={() => setSelectedSlot(null)}
                  className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> {t("Αλλαγή ώρας", "Change time")}
                </button>
                <div className="rounded-lg border border-primary/40 bg-primary/5 px-4 py-3 text-sm">
                  <div className="font-semibold">{formatDayLabel(selectedDate!)} | {selectedSlot.label}</div>
                  <div className="text-muted-foreground">{t("Διάρκεια 30 λεπτά | Google Meet", "30 minutes | Google Meet")}</div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="wb-name">{t("Ονοματεπώνυμο", "Full name")}</Label>
                    <Input id="wb-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={150} required />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="wb-email">Email</Label>
                    <Input id="wb-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={255} required />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="wb-company">{t("Εταιρεία", "Company")}</Label>
                    <Input id="wb-company" value={company} onChange={(e) => setCompany(e.target.value)} maxLength={200} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="wb-phone">{t("Τηλέφωνο", "Phone")}</Label>
                    <Input id="wb-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={40} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="wb-notes">{t("Σημειώσεις (προαιρετικό)", "Notes (optional)")}</Label>
                  <Textarea id="wb-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} maxLength={1500} />
                </div>

                <Button onClick={submit} disabled={submitting} className="w-full h-11 text-base">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarDays className="w-4 h-4 mr-2" />}
                  {t("Επιβεβαίωση ραντεβού", "Confirm booking")}
                </Button>
                <p className="text-[11px] text-center text-muted-foreground">
                  {t(
                    "Θα λάβετε πρόσκληση Google Calendar με σύνδεσμο Google Meet.",
                    "You'll get a Google Calendar invite with a Google Meet link."
                  )}
                </p>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}